<template>
  <section>
    <spinner v-if="isLoading"></spinner>
    <div class="content-wrapper">
      <div class="content-heading">
        {{ type === 'transaction' ? 'Transaction Notices' : 'Documents' }}
        <small data-localize="dashboard.WELCOME"></small>
      </div>
       <div class="panel panel-default">
         <!-- Desktop Table View -->
         <div class="table-responsive top-margin table-fixed-header--md desktop-table">
          <table class="table table-striped">
            <thead>
              <tr>
                <th class="text-left">Name</th>
                <th class="text-right with-right-padding">{{ type === 'transaction' ? 'Sent on' : 'Uploaded on' }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="document in documents" :key="document.Key">
                <td class="file file-name td-blocked" @click="openDocument(document)"><i class="icon fa fa-file-text-o"></i><a>{{document.Key}}</a></td>
                <td class="file-updated-date text-right with-right-padding td-blocked">{{formatDate(
                  !!document.Head  &&
                  !!document.Head.Metadata  &&
                  !!document.Head.Metadata.uploaded_at ?
                  document.Head.Metadata.uploaded_at : document.LastModified)}}</td>
              </tr>
              <tr v-if="!documents.length" class="align-center">
                <td class="file file-name td-blocked">No Documents to display</td>
              </tr>
            </tbody>
          </table>
         </div>

         <!-- Mobile Card View -->
         <div class="mobile-card-view">
           <div v-if="documents.length" class="document-cards">
             <div 
               v-for="document in documents" 
               :key="document.Key"
               class="document-card"
               @click="openDocument(document)"
             >
               <div class="document-card-icon">
                 <i class="icon fa fa-file-text-o"></i>
               </div>
               <div class="document-card-content">
                 <div class="document-card-name">{{document.Key}}</div>
                 <div class="document-card-date">
                   {{ type === 'transaction' ? 'Sent on' : 'Uploaded on' }}: 
                   {{formatDate(
                     !!document.Head  &&
                     !!document.Head.Metadata  &&
                     !!document.Head.Metadata.uploaded_at ?
                     document.Head.Metadata.uploaded_at : document.LastModified)}}
                 </div>
               </div>
               <div class="document-card-arrow">
                 <i class="fa fa-chevron-right"></i>
               </div>
             </div>
           </div>
           <div v-else class="no-documents-mobile">
             <i class="fa fa-folder-open-o"></i>
             <p>No Documents to display</p>
           </div>
         </div>
       </div>
    </div>
  </section>
</template>

<script>
import { logAction, formatDate, openUrl } from '@/util';
import logActions from '@/data/static/logActions';
import { mapActions, mapGetters, mapState } from 'vuex';
import spinner from '@/components/widgets/Spinner';
import moment from 'moment';

const mix = {
  methods: {
    formatDate,
  },
};
export default {
  name: 'AccountDocuments',
  async beforeMount() {
    if (!this.hasBeenCreatedAccount) {
      this.$router.push({ name: 'AccountSummary' });
      return;
    }
    await this.checkUserStatus();
    await this.fetchDocuments();
    this.isLoading = false;
    this.type = this.$route.query.type;
  },
  data() {
    return {
      documents: [],
      isLoading: true,
      type: '',
    };
  },
  mixins: [mix],
  methods: {
    ...mapActions('account', [
      'getAccountDocuments',
      'createDocumentView',
      'createAccountDocumentPresignedUrl',
    ]),
    ...mapActions('home', [
      'getUserStatus',
    ]),
    async logDocumentViewed(doc) {
      const { Key, LastModified, ETag, Url } = doc;
      await this.createDocumentView({
        fileName: Key,
        lastModified: moment(LastModified).toDate(),
        url: Url.split('?')[0],
        Etag: ETag,
        userId: this.user.id,
        accountId: this.activeAccountId,
      });
      const logMessage = `User U${this.user.id} viewed ${Key} document`;
      logAction({
        user: this.user.id,
        level: 'info',
        action: logActions.DOCUMENT_VIEW.name,
        message: logMessage,
      });
    },
    async openDocument(doc) {
      const res = await this.createAccountDocumentPresignedUrl({
        accountId: this.activeAccountId,
        payload: {
          key: doc.OriginalKey,
        },
      });
      openUrl(res.url);

      await this.logDocumentViewed({ ...doc, Url: res.url });
    },
    async checkUserStatus() {
      try {
        const accountId = this.activeAccount.id;

        // Without a real account there is no status to fetch. Asking for
        // account 0 returns a 403, which the HttpService interceptor turns
        // into a redirect to Home.
        if (!accountId) {
          return;
        }

        const response = await this.getUserStatus({
          accountId,
        });
        const { status, needs } = response;

        if (status === 'document') {
          this.$router.push({ name: 'DocumentUpload' });
        } else if (status === 'retry') {
          this.$router.push({ name: 'AccountCreation' });
        } else if (status === 'suspended') {
          this.$router.push({ name: 'AccountSuspended' });
        } else if (!needs.includes('FUNDING_SOURCE') && needs.includes('NOTE')) {
          this.$router.push({ name: 'PurchaseNote', query: { first: 1 } });
        }
      } catch (error) {
        console.error(error);
      }
    },
    async fetchDocuments(query = '') {
      this.documents = await this.getAccountDocuments({
        accountId: this.activeAccountId,
        query: { documentType: query || this.$route.query.type || 'reports' },
      });
      this.type = query || this.$route.query.type;
    },
  },
  computed: {
    ...mapState('app', [
      'activeAccountId',
      'activeAccount',
      'user',
    ]),
    ...mapGetters('app', [
      'hasBeenCreatedAccount',
    ]),
  },
  components: {
    spinner,
  },
  watch: {
    $route(to) {
      this.isLoading = true;
      this.fetchDocuments(to.query.type).then(() => {
        this.isLoading = false;
      });
    },
  },
};
</script>

<style scoped>
  /* Desktop table styles */
  table {
    border-collapse: collapse;
    font-size: 16px;
  }
  
  th {
    padding: 14px 8px;
    color: #888;
    border-bottom-width: 1px;
    vertical-align: bottom;
    border-bottom: 1px solid #eee;
    border-top: 0;
    line-height: 1.52857143;
    font-weight: normal;
  }
  
  td {
    padding: 8px;
    line-height: 1.52857143;
    vertical-align: top;
    border-top: 1px solid #eee;
    height: 60px;
  }
  
  .table-striped>tbody>tr:nth-of-type(odd) {
    background-color: #fafbfc;
  }

  tbody tr td:last-child {
    padding-top: 10px;
  }

  .td-blocked {
    display: block;
  }

  .icon {
    margin-right: 5px;
    color: #00788d;
  }

  .file-name a {
    color: #1c8799;
    font-weight: 600;
    text-decoration: underline;
  }

  .file-updated-date {
    color: #6d6d6d;
  }

  .file-name:hover {
    cursor: pointer;
  }

  .with-right-padding {
    padding-right: 60px;
  }

  .table-fixed-header--md {
    min-height: 600px;
    height: 100%;
  }

  .table-fixed-header--md tbody {
    height: 600px;
  }

  .align-center {
    text-align-last: center;
  }

  /* Mobile card view styles - hidden on desktop */
  .mobile-card-view {
    display: none;
  }

  /* Desktop table view - visible by default */
  .desktop-table {
    display: block;
  }

  /* Mobile responsive styles */
  @media (max-width: 1024px) {
    /* Hide desktop table, show mobile cards */
    .desktop-table {
      display: none !important;
    }

    .mobile-card-view {
      display: block;
      padding: 0;
    }

    .document-cards {
      display: flex;
      flex-direction: column;
      gap: 0;
    }

    .document-card {
      display: flex;
      align-items: center;
      padding: 16px 20px;
      background-color: #fff;
      border-bottom: 1px solid #eee;
      cursor: pointer;
      transition: background-color 0.2s;
      min-height: 72px;
    }

    .document-card:nth-child(odd) {
      background-color: #fafbfc;
    }

    .document-card:hover,
    .document-card:active {
      background-color: #f0f7ff;
    }

    .document-card-icon {
      flex-shrink: 0;
      margin-right: 16px;
      font-size: 24px;
      color: #00788d;
    }

    .document-card-content {
      flex: 1;
      min-width: 0;
    }

    .document-card-name {
      color: #1c8799;
      font-weight: 600;
      font-size: 16px;
      line-height: 1.4;
      word-break: break-word;
      margin-bottom: 6px;
    }

    .document-card-date {
      color: #6d6d6d;
      font-size: 14px;
      line-height: 1.3;
    }

    .document-card-arrow {
      flex-shrink: 0;
      margin-left: 12px;
      color: #ccc;
      font-size: 14px;
    }

    .no-documents-mobile {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 60px 20px;
      text-align: center;
      color: #999;
    }

    .no-documents-mobile i {
      font-size: 48px;
      margin-bottom: 16px;
      color: #ddd;
    }

    .no-documents-mobile p {
      font-size: 16px;
      margin: 0;
      color: #999;
    }

    .content-heading {
      font-size: 20px !important;
      padding: 20px 20px 16px 20px !important;
    }

    .panel {
      margin-bottom: 0 !important;
    }
  }

  @media (max-width: 480px) {
    .document-card {
      padding: 14px 16px;
      min-height: 68px;
    }

    .document-card-icon {
      font-size: 22px;
      margin-right: 14px;
    }

    .document-card-name {
      font-size: 15px;
    }

    .document-card-date {
      font-size: 13px;
    }

    .content-heading {
      font-size: 18px !important;
      padding: 18px 16px 14px 16px !important;
    }

    .no-documents-mobile {
      padding: 50px 16px;
    }

    .no-documents-mobile i {
      font-size: 40px;
    }

    .no-documents-mobile p {
      font-size: 15px;
    }
  }

  @media (max-width: 360px) {
    .document-card {
      padding: 12px 14px;
    }

    .document-card-icon {
      font-size: 20px;
      margin-right: 12px;
    }

    .document-card-name {
      font-size: 14px;
    }

    .document-card-date {
      font-size: 12px;
    }

    .content-heading {
      font-size: 17px !important;
      padding: 16px 14px 12px 14px !important;
    }
  }
</style>



// WEBPACK FOOTER //
// AccountDocuments.vue?2646fae0